export const translations = {
  en: {
    common: {
      signIn: "Sign in", signOut: "Sign out", getStarted: "Get started", startFree: "Start free",
      cancel: "Cancel", delete: "Delete", save: "Save", export: "Export", search: "Search…",
      loading: "Loading…", details: "Details", view: "View", add: "Add", notifications: "Notifications",
      active: "active", allClear: "All clear — nothing needs attention.", createOne: "Create one",
      noAccount: "No account?", haveAccount: "Already have an account?", or: "or", email: "Email",
      password: "Password", fullName: "Full name", continueGoogle: "Continue with Google",
      family: "Explore the WorkSafe family", familyShort: "Family",
    },
    nav: {
      dashboard: "Dashboard", incidents: "Incidents", compliance: "Compliance",
      documents: "Documents", assistant: "AI Assistant", knowledge: "Knowledge Base",
      reports: "Reports", settings: "Settings",
    },
    auth: {
      welcomeBack: "Welcome back", signInSub: "Sign in to your compliance workspace.",
      createAccount: "Create your account", createSub: "Start tracking environmental compliance in minutes.",
      heroTitle: "Environmental compliance, closed-loop and AI-assisted.",
      heroSub: "Log spills, air, water and waste issues with suggested EPA and OSHA references, reviewed by a person.",
      inclusiveTitle: "Built for everyone, inclusive by design.",
      inclusiveSub: "Accessible, plain-language compliance tooling for teams of every size.",
    },
    dash: {
      welcome: "Welcome back", subtitle: "Your environmental compliance overview.",
      openIncidents: "Open Incidents", overdueItems: "Overdue Items", documents: "Documents", compliance: "Compliance",
      total: "total", dueSoon: "due soon", analyzedStored: "analyzed & stored", onTrackRate: "on-track rate",
      liveConditions: "Live Conditions", incidentsByType: "Incidents by Type", lifecyclePipeline: "Lifecycle Pipeline",
      noIncidents: "No incidents logged yet.", logReading: "Log reading", threshold: "threshold",
      over: "Over", ok: "OK", addReading: "Log Sensor Reading", sensor: "Sensor", value: "Value",
      updated: "updated", readingSaved: "Reading saved.",
      breach: "⚠ Threshold exceeded for this sensor.", incidentCreated: "A draft incident was auto-created.",
      auto: "Auto",
      simulate: "Simulate device", simulating: "Streaming…", simDone: "Device simulation complete.",
      history: "Sensor History", historyDesc: "Persisted readings over time with the active threshold.", noHistory: "No readings recorded yet for this sensor.", viewHistory: "View history", latest: "Latest", readings: "readings", breaches: "breaches", exportCsv: "Export CSV",
    },
    inc: {
      title: "Incidents", subtitle: "Log environmental events and drive them to closure.",
      new: "New Incident", reportTitle: "Report Environmental Incident",
      reportDesc: "Log a spill, emission, discharge or other environmental event for AI tagging.",
      logIncident: "Log Incident", loggingTagging: "Logging & tagging…", noIncidents: "No incidents yet. Log your first environmental event.",
      aiSummary: "AI Summary", citations: "Regulatory Citations", recommendedActions: "Recommended Actions", lifecycle: "Lifecycle",
      detailsDesc: "Incident details, AI-suggested citations and lifecycle history.",
      searchPlaceholder: "Search incidents…", allStatuses: "All statuses", allTypes: "All types", noMatch: "No incidents match your filters.",
    },
    comp: {
      title: "Compliance Calendar", subtitle: "Track recurring obligations, permits and filing deadlines.",
      new: "New Obligation", addTitle: "Add Compliance Obligation", addDesc: "Track a recurring obligation, permit or filing deadline.",
      addObligation: "Add Obligation", noItems: "No obligations tracked yet.",
    },
    doc: {
      title: "Documents", subtitle: "Upload permits & reports for AI compliance analysis.",
      dropHint: "Drop a file or click to upload", dropTypes: "PDF, TXT, CSV, MD up to ~10MB",
      uploading: "Uploading…", noDocs: "No documents yet.", analyze: "Analyze", analyzing: "Analyzing…",
      view: "View", analyzed: "analyzed", aiAnalysis: "AI Analysis", analysisDesc: "AI-generated compliance summary, obligations, gaps and citations.",
    },
    asst: {
      title: "EnviSafe AI Assistant", askTitle: "Ask about environmental compliance",
      askSub: "EPA & OSHA rules, spills, air, water, waste — with cited answers.",
      placeholder: "Ask about EPA / OSHA environmental compliance…", newChat: "New chat",
      s1: "When do I need an SPCC plan for diesel storage?",
      s2: "What are the NPDES stormwater requirements for construction?",
      s3: "How should we handle an asbestos discovery during demolition?",
      s4: "What WBGT level triggers heat illness controls?",
    },
    kb: {
      title: "Knowledge Base", subtitle: "Quick reference for key U.S. environmental regulations across EPA and OSHA.",
      searchPlaceholder: "Search regulations…", noMatch: "No matching regulations.",
    },
    settings: {
      title: "Settings", subtitle: "Tune sensor thresholds to your permit conditions and manage your profile.",
      profile: "Profile", name: "Display name", email: "Email", authMethod: "Sign-in method", saveProfile: "Save profile", profileSaved: "Profile updated.",
      thresholds: "Sensor Thresholds", thresholdsHint: "Readings at or above these limits auto-create a draft incident.",
      defaultLabel: "default", save: "Save thresholds", saved: "Thresholds saved.", reset: "Reset to defaults",
      integrations: "API & IoT Integration", apiKey: "Your API key",
      apiHint: "Let external sensors or systems push readings into EnviSafe. Readings at or above your thresholds auto-create a draft incident.",
      reveal: "Reveal", hide: "Hide", copy: "Copy", copied: "Copied!", regenerate: "Regenerate", regenerated: "API key regenerated.",
      endpoint: "Ingestion endpoint", example: "Example request",
    },
    reports: {
      title: "Reports", subtitle: "Environmental compliance analytics and trends.",
      range7: "7 days", range30: "30 days", range90: "90 days",
      incidentsOverTime: "Incidents Over Time", severityDist: "Severity Distribution",
      resolutionRate: "Resolution Rate", avgResolution: "Avg. Resolution", autoGenerated: "Auto-generated",
      completedItems: "Obligations Completed", hours: "hrs", openIncidents: "Open Incidents", totalIncidents: "Total Incidents",
      noData: "No data for this period yet.", exportReport: "Export report (CSV)",
    },
    fields: {
      title: "Title", type: "Type", severity: "Severity", site: "Site", location: "Location",
      description: "Description", category: "Category", dueDate: "Due date", regulation: "Regulation",
      regulationOpt: "Regulation (optional)", notes: "Notes", notesOpt: "Notes (optional)",
    },
    status: { open: "Open", reported: "Reported", contained: "Contained", resolved: "Resolved", closed: "Closed" },
    due: { overdue: "Overdue", dueSoon: "Due soon", pending: "Pending", completed: "Completed" },
    severity: { low: "Low", medium: "Medium", high: "High", critical: "Critical" },
    subtype: {
      petroleum_spill: "Petroleum Spill", chemical_spill: "Chemical Spill", stormwater_discharge: "Stormwater Discharge",
      air_emission: "Air Emission", waste_mishandling: "Waste Mishandling", asbestos: "Asbestos",
      heat_stress: "Heat Stress", noise_exceedance: "Noise Exceedance", voc_release: "VOC Release", other: "Other",
    },
    category: {
      all: "All", air: "Air Quality", water: "Water / Stormwater", waste: "Waste / RCRA", spill: "Spill / SPCC",
      permit: "Permit Renewal", reporting: "Reporting / Filing", training: "Training", general: "General",
    },
    kbcat: { all: "All", air: "Air", water: "Water", spill: "Spills", waste: "Waste", asbestos: "Asbestos", noise: "Noise", heat: "Heat", chemical: "Chemical" },
  },
  es: {
    common: {
      signIn: "Iniciar sesión", signOut: "Cerrar sesión", getStarted: "Empezar", startFree: "Comienza gratis",
      cancel: "Cancelar", delete: "Eliminar", save: "Guardar", export: "Exportar", search: "Buscar…",
      loading: "Cargando…", details: "Detalles", view: "Ver", add: "Agregar", notifications: "Notificaciones",
      active: "activas", allClear: "Todo en orden — nada requiere atención.", createOne: "Crear una",
      noAccount: "¿Sin cuenta?", haveAccount: "¿Ya tienes una cuenta?", or: "o", email: "Correo",
      password: "Contraseña", fullName: "Nombre completo", continueGoogle: "Continuar con Google",
      family: "Explora la familia WorkSafe", familyShort: "Familia",
    },
    nav: {
      dashboard: "Panel", incidents: "Incidentes", compliance: "Cumplimiento",
      documents: "Documentos", assistant: "Asistente IA", knowledge: "Base de Conocimiento",
      reports: "Reportes", settings: "Ajustes",
    },
    auth: {
      welcomeBack: "Bienvenido de nuevo", signInSub: "Inicia sesión en tu espacio de cumplimiento.",
      createAccount: "Crea tu cuenta", createSub: "Empieza a gestionar el cumplimiento ambiental en minutos.",
      heroTitle: "Cumplimiento ambiental, en ciclo cerrado y asistido por IA.",
      heroSub: "Registra derrames, aire, agua y residuos con referencias sugeridas de EPA y OSHA, revisadas por una persona.",
      inclusiveTitle: "Hecho para todos, inclusivo por diseño.",
      inclusiveSub: "Herramientas de cumplimiento accesibles y en lenguaje claro para equipos de todo tamaño.",
    },
    dash: {
      welcome: "Bienvenido de nuevo", subtitle: "Tu resumen de cumplimiento ambiental.",
      openIncidents: "Incidentes Abiertos", overdueItems: "Tareas Vencidas", documents: "Documentos", compliance: "Cumplimiento",
      total: "en total", dueSoon: "próximas", analyzedStored: "analizados y guardados", onTrackRate: "tasa al día",
      liveConditions: "Condiciones en Vivo", incidentsByType: "Incidentes por Tipo", lifecyclePipeline: "Flujo del Ciclo",
      noIncidents: "Aún no hay incidentes registrados.", logReading: "Registrar lectura", threshold: "umbral",
      over: "Excede", ok: "OK", addReading: "Registrar Lectura de Sensor", sensor: "Sensor", value: "Valor",
      updated: "actualizado", readingSaved: "Lectura guardada.",
      breach: "⚠ Umbral excedido para este sensor.", incidentCreated: "Se creó un incidente borrador automáticamente.",
      auto: "Auto",
      simulate: "Simular dispositivo", simulating: "Transmitiendo…", simDone: "Simulación de dispositivo completada.",
      history: "Historial del Sensor", historyDesc: "Lecturas guardadas a lo largo del tiempo con el umbral activo.", noHistory: "Aún no hay lecturas registradas para este sensor.", viewHistory: "Ver historial", latest: "Última", readings: "lecturas", breaches: "excedencias", exportCsv: "Exportar CSV",
    },
    inc: {
      title: "Incidentes", subtitle: "Registra eventos ambientales y llévalos hasta su cierre.",
      new: "Nuevo Incidente", reportTitle: "Reportar Incidente Ambiental",
      reportDesc: "Registra un derrame, emisión, descarga u otro evento ambiental para etiquetado con IA.",
      logIncident: "Registrar Incidente", loggingTagging: "Registrando y etiquetando…", noIncidents: "Aún no hay incidentes. Registra tu primer evento ambiental.",
      aiSummary: "Resumen IA", citations: "Citas Normativas", recommendedActions: "Acciones Recomendadas", lifecycle: "Ciclo de Vida",
      detailsDesc: "Detalles del incidente, citas sugeridas por IA e historial del ciclo de vida.",
      searchPlaceholder: "Buscar incidentes…", allStatuses: "Todos los estados", allTypes: "Todos los tipos", noMatch: "Ningún incidente coincide con los filtros.",
    },
    comp: {
      title: "Calendario de Cumplimiento", subtitle: "Rastrea obligaciones recurrentes, permisos y plazos de presentación.",
      new: "Nueva Obligación", addTitle: "Agregar Obligación de Cumplimiento", addDesc: "Rastrea una obligación recurrente, permiso o plazo de presentación.",
      addObligation: "Agregar Obligación", noItems: "Aún no hay obligaciones registradas.",
    },
    doc: {
      title: "Documentos", subtitle: "Sube permisos e informes para análisis de cumplimiento con IA.",
      dropHint: "Suelta un archivo o haz clic para subir", dropTypes: "PDF, TXT, CSV, MD hasta ~10MB",
      uploading: "Subiendo…", noDocs: "Aún no hay documentos.", analyze: "Analizar", analyzing: "Analizando…",
      view: "Ver", analyzed: "analizado", aiAnalysis: "Análisis IA", analysisDesc: "Resumen de cumplimiento, obligaciones, brechas y citas generados por IA.",
    },
    asst: {
      title: "Asistente IA EnviSafe", askTitle: "Pregunta sobre cumplimiento ambiental",
      askSub: "Reglas de EPA y OSHA, derrames, aire, agua, residuos — con respuestas citadas.",
      placeholder: "Pregunta sobre cumplimiento ambiental de EPA / OSHA…", newChat: "Nuevo chat",
      s1: "¿Cuándo necesito un plan SPCC para almacenamiento de diésel?",
      s2: "¿Cuáles son los requisitos de aguas pluviales NPDES para construcción?",
      s3: "¿Cómo manejar un hallazgo de asbesto durante una demolición?",
      s4: "¿Qué nivel de WBGT activa los controles de enfermedad por calor?",
    },
    kb: {
      title: "Base de Conocimiento", subtitle: "Referencia rápida de las principales normativas ambientales de EE. UU. (EPA y OSHA).",
      searchPlaceholder: "Buscar normativas…", noMatch: "No hay normativas coincidentes.",
    },
    settings: {
      title: "Ajustes", subtitle: "Ajusta los umbrales de sensores a tus condiciones de permiso y gestiona tu perfil.",
      profile: "Perfil", name: "Nombre visible", email: "Correo", authMethod: "Método de inicio", saveProfile: "Guardar perfil", profileSaved: "Perfil actualizado.",
      thresholds: "Umbrales de Sensores", thresholdsHint: "Las lecturas iguales o superiores a estos límites crean un incidente borrador automáticamente.",
      defaultLabel: "predeterminado", save: "Guardar umbrales", saved: "Umbrales guardados.", reset: "Restablecer",
      integrations: "API e Integración IoT", apiKey: "Tu clave API",
      apiHint: "Permite que sensores o sistemas externos envíen lecturas a EnviSafe. Las lecturas iguales o superiores a tus umbrales crean un incidente borrador automáticamente.",
      reveal: "Mostrar", hide: "Ocultar", copy: "Copiar", copied: "¡Copiado!", regenerate: "Regenerar", regenerated: "Clave API regenerada.",
      endpoint: "Punto de ingestión", example: "Solicitud de ejemplo",
    },
    reports: {
      title: "Reportes", subtitle: "Análisis y tendencias de cumplimiento ambiental.",
      range7: "7 días", range30: "30 días", range90: "90 días",
      incidentsOverTime: "Incidentes en el Tiempo", severityDist: "Distribución por Gravedad",
      resolutionRate: "Tasa de Resolución", avgResolution: "Resolución Prom.", autoGenerated: "Autogenerados",
      completedItems: "Obligaciones Completadas", hours: "h", openIncidents: "Incidentes Abiertos", totalIncidents: "Incidentes Totales",
      noData: "Aún no hay datos para este período.", exportReport: "Exportar reporte (CSV)",
    },
    fields: {
      title: "Título", type: "Tipo", severity: "Gravedad", site: "Sitio", location: "Ubicación",
      description: "Descripción", category: "Categoría", dueDate: "Fecha límite", regulation: "Normativa",
      regulationOpt: "Normativa (opcional)", notes: "Notas", notesOpt: "Notas (opcional)",
    },
    status: { open: "Abierto", reported: "Reportado", contained: "Contenido", resolved: "Resuelto", closed: "Cerrado" },
    due: { overdue: "Vencida", dueSoon: "Próxima", pending: "Pendiente", completed: "Completada" },
    severity: { low: "Baja", medium: "Media", high: "Alta", critical: "Crítica" },
    subtype: {
      petroleum_spill: "Derrame de petróleo", chemical_spill: "Derrame químico", stormwater_discharge: "Descarga pluvial",
      air_emission: "Emisión al aire", waste_mishandling: "Mal manejo de residuos", asbestos: "Asbesto",
      heat_stress: "Estrés térmico", noise_exceedance: "Exceso de ruido", voc_release: "Liberación de COV", other: "Otro",
    },
    category: {
      all: "Todas", air: "Calidad del aire", water: "Agua / Pluvial", waste: "Residuos / RCRA", spill: "Derrame / SPCC",
      permit: "Renovación de permiso", reporting: "Reportes / Presentación", training: "Capacitación", general: "General",
    },
    kbcat: { all: "Todas", air: "Aire", water: "Agua", spill: "Derrames", waste: "Residuos", asbestos: "Asbesto", noise: "Ruido", heat: "Calor", chemical: "Químicos" },
  },
};
